import { Link } from 'react-router-dom';
import ResumeCard from '../../../components/cards/ResumeCard';
import ErrorMessage from '../../../components/messages/ErrorMessage';
import { DashboardPolicySummary } from '../../../Interfaces/Insurance/Policy';
import Table from '../../../components/Table/Table';
import TooltipContent from '../../../components/messages/TooltipContent';
import PolicyAttachment from './PolicyAttachment';
import { Column } from 'primereact/column';
import { formatCurrency, formatDate } from '@/src/utils/formatUtils';
import WrapperButton from '../../../components/buttons/WrapperButton';
import ClaimTableDrilldown from '../claims/summary/ClaimTableDrilldown';
import { useState } from 'react';

export type PolicySummaryProps = {
  isError: boolean;
  policies: DashboardPolicySummary[];
};

const PolicySummary = ({ isError, policies }: PolicySummaryProps) => {
  const [showDrilldown, setShowDrilldown] = useState(false);
  const [policy, setSelectedPolicy] = useState<DashboardPolicySummary>();

  const onCellClick = (policy: DashboardPolicySummary) => {
    setSelectedPolicy(policy);
    setShowDrilldown(true);
  };

  const hideSelectedDrilldown = () => {
    setSelectedPolicy(undefined);
    setShowDrilldown(false);
  };

  if (isError) {
    return (
      <ResumeCard>
        <div className="relative p-5 px-4 flex flex-column h-full">
          <Link to={`/insurance/policies`} className="w-fit">
            <div className="text-24px font-bold cursor-pointer totalColor hover:underline w-fit">
              Active Policies
            </div>
          </Link>
          <div className="my-8 py-3">
            <ErrorMessage
              content={'Failed to obtain data! Please try again later.'}
            />
          </div>
        </div>
      </ResumeCard>
    );
  }

  return (
    <ResumeCard>
      <div className="p-3 px-4 flex flex-column">
        <Link to={`/insurance/policies`} className="w-fit">
          <div className="text-24px font-bold cursor-pointer totalColor hover:underline">
            Active Policies
          </div>
        </Link>
        <div className="pt-3 text-xl flex flex-wrap">
          {policy && (
            <ClaimTableDrilldown
              visible={showDrilldown}
              hideDrilldown={hideSelectedDrilldown}
              policy={{
                id: policy.id,
                policy: policy.policy,
                termStart: policy.termStart,
                termEnd: policy.termEnd,
              }}
              showActiveColumn={true}
              title={'Claims in Active Policy'}
            />
          )}
          <Table
            data={policies}
            className={`dashboardOptionShadow noFooter w-full`}
            fixedDefaultHeight={187}
          >
            <Column
              field="policy"
              header="Policy"
              body={(policy) => (
                <div className="scroll-text">{policy.policy}</div>
              )}
              headerClassName={`tableHeader font-normal justify-content-center`}
              style={{ minWidth: '200px', maxWidth: '200px' }}
              className={`text-standard blackText tableCell overflow-x-hidden white-space-nowrap checkOverflow`}
              footerClassName="tableFooter"
            />

            <Column
              field="termStart"
              header="Effective Date"
              style={{ minWidth: '140px' }}
              body={(policy) => {
                return formatDate(policy.termStart);
              }}
              headerClassName="tableHeader font-normal"
              className="justify-content-center text-standard blackText tableCell"
              footerClassName="tableFooter"
            />
            <Column
              field="termEnd"
              header="Expiration Date"
              style={{ minWidth: '140px' }}
              body={(policy) => {
                return formatDate(policy.termEnd);
              }}
              headerClassName="tableHeader font-normal"
              className="justify-content-center text-standard blackText tableCell"
              footerClassName="tableFooter"
            />
            <Column
              field="limit"
              header="Limit"
              body={(policy) => formatCurrency(policy.limit)}
              style={{ minWidth: '120px' }}
              headerClassName="tableHeader font-normal"
              className="justify-content-center text-standard blackText tableCell"
              footerClassName="tableFooter"
            />
            <Column
              field="claims"
              header="Claims"
              body={(policy) =>
                policy.claims ? (
                  <WrapperButton
                    className="totalColor hover:underline"
                    onClick={() => onCellClick(policy)}
                  >
                    {policy.claims}
                  </WrapperButton>
                ) : (
                  policy.claims
                )
              }
              style={{ minWidth: '100px' }}
              headerClassName="tableHeader font-normal"
              className="justify-content-center text-standard blackText tableCell"
              footerClassName="tableFooter"
            />
            <Column
              style={{ minWidth: '40px', maxWidth: '40px' }}
              body={(policy) =>
                policy.attachmentName ? (
                  <PolicyAttachment
                    policyID={policy.id}
                    attachmentName={policy.attachmentName ?? ''}
                    showIcon={true}
                  />
                ) : (
                  <div className="text-red-600 font-bold">
                    <TooltipContent
                      label="!"
                      content={`No attachment file was found!`}
                      id={policy.id.replace(/-/g, '')}
                    />
                  </div>
                )
              }
              headerClassName="tableHeader font-normal"
              className="justify-content-center text-standard blackText tableCell printHide"
              footerClassName="tableFooter"
            />
          </Table>
        </div>
      </div>
    </ResumeCard>
  );
};

export default PolicySummary;
